import {
  closeCrossWindow2dReferenceLines,
  createActiveViewportContext,
  initializeCrossWindow2dReferenceLines,
  setCrossWindow2dReferenceLinesEnabled,
  setCrossWindowActiveViewport,
  setCrossWindowSelectedTool,
} from '../components/App/Viewer/crossWindow2dReferenceLines';
import { getAvailableRenderers } from '../model/getAvailableRenderers';
import { CHILD_VIEWPORT_WINDOW_NAME_PREFIX } from './isChildViewport';

let childSyncChannelId;
let openedChildWindows = [];
const CHILD_VIEWPORT_STATE_PREFIX = 'childViewportState:';

export const closeChildViewports = () => {
  openedChildWindows.forEach(({ window: childWindow }) => {
    if (childWindow && !childWindow.closed) childWindow.close();
  });
  openedChildWindows = [];
  childSyncChannelId = undefined;
  closeCrossWindow2dReferenceLines();
};

const getLayoutSnapshot = (layout, waveform, syncChannel, targetIndex, seriesToDisplay, appConfig) => {
  let mode = '2d';
  if (layout.isMPRModeEnabled) mode = 'mpr';
  else if (layout.is3DModeEnabled) mode = '3d';
  const appConfigSnapshot = { ...appConfig };
  delete appConfigSnapshot.hangingProtocols;

  return {
    appConfig: appConfigSnapshot,
    columns: layout.columns,
    rows: layout.rows,
    selectedViewIndex: targetIndex,
    currentTool: layout.currentTool,
    syncChannel,
    mode,
    elements: layout.elements.map((originalElement, index) => {
      const element = index === targetIndex
        ? {
          ...originalElement,
          StudyInstanceUID: seriesToDisplay.study.StudyInstanceUID,
          SeriesInstanceUID: seriesToDisplay.series.SeriesInstanceUID,
          availableRenderers: seriesToDisplay.series.instances?.length
            ? getAvailableRenderers(seriesToDisplay.series.instances)
            : originalElement.availableRenderers || [],
        }
        : originalElement;
      if (!element?.StudyInstanceUID || !element?.SeriesInstanceUID) return null;
      const camera = element.initOptions?.viewType?.camera;
      let elementMode;
      if (mode === '2d') {
        elementMode = element.renderType === 'waveform' ? 'ecg' : '2d';
      } else {
        elementMode = element.initOptions?.viewType?.type || (mode === 'mpr' ? 'mpr' : 'vr');
      }
      // Empty panes in MPR/3D layouts have no camera and should stay empty.
      if (mode !== '2d' && !camera) return null;
      return {
        studyUID: element.StudyInstanceUID,
        seriesUID: element.SeriesInstanceUID,
        renderType: element.renderType,
        availableRenderers: element.availableRenderers || [],
        mode: elementMode,
        camera,
      };
    }),
    waveform: {
      layout: waveform.waveformLayout,
      viewIndex: waveform.waveformLayoutViewIndex,
      scaleFactor: waveform.scaleFactor,
      timeFactor: waveform.timeFactor,
    },
  };
};

const chooseSeriesToDisplay = (layout, state) => {
  const { patient } = state;
  const selectedElement = layout.elements[layout.selectedViewIndex];
  const preferredStudyUID = selectedElement?.StudyInstanceUID || patient?.mainStudyId;
  const preferredStudy = patient?.studies?.find((study) => study.StudyInstanceUID === preferredStudyUID)
    || patient?.studies?.find((study) => study.StudyInstanceUID === patient?.mainStudyId)
    || patient?.studies?.[0];
  if (!preferredStudy) return null;

  const seriesList = (preferredStudy.series || []).filter((series) => series?.SeriesInstanceUID);
  if (seriesList.length === 0) return null;

  openedChildWindows = openedChildWindows.filter(({ window: childWindow }) => childWindow && !childWindow.closed);
  const displayedSeries = new Set(
    layout.elements.map((element) => element?.SeriesInstanceUID).filter(Boolean)
  );
  const assignedSeries = new Set(openedChildWindows.map(({ seriesUID }) => seriesUID));
  const firstFreeSeries = seriesList.find((series) => (
    !displayedSeries.has(series.SeriesInstanceUID)
    && !assignedSeries.has(series.SeriesInstanceUID)
  ));

  return {
    study: preferredStudy,
    series: firstFreeSeries || seriesList[0],
  };
};

const getViewportUrl = (studyUID, layoutKey) => {
  const basePath = window.location.pathname.split('/viewer/')[0].replace(/\/$/, '');
  const studyPath = `${basePath}/viewer/study/${encodeURIComponent(studyUID)}`;
  const query = new URLSearchParams({ sid: layoutKey });

  return `${window.location.origin}${studyPath}?${query.toString()}`;
};

export const openChildViewport = (layout, state) => {
  const { waveform } = state;
  const seriesToDisplay = chooseSeriesToDisplay(layout, state);
  if (!seriesToDisplay) return;
  const selectedIndexHasSeries = layout.elements[layout.selectedViewIndex]?.SeriesInstanceUID;
  const targetIndex = selectedIndexHasSeries
    ? layout.selectedViewIndex
    : Math.max(0, layout.elements.findIndex((element) => element?.SeriesInstanceUID));

  const currentScreen = window.screen;
  const left = currentScreen.availLeft ?? currentScreen.left ?? 0;
  const top = currentScreen.availTop ?? currentScreen.top ?? 0;
  const width = currentScreen.availWidth || window.innerWidth;
  const height = currentScreen.availHeight || window.innerHeight;
  const popupFeatures = `popup=yes,location=no,toolbar=no,menubar=no,status=no,left=${left},top=${top},width=${width},height=${height}`;
  childSyncChannelId = childSyncChannelId
    || (window.crypto?.randomUUID && window.crypto.randomUUID())
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const syncChannel = childSyncChannelId;
  const layoutKey = (window.crypto?.randomUUID && window.crypto.randomUUID())
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const layoutSnapshot = getLayoutSnapshot(layout, waveform, syncChannel, targetIndex, seriesToDisplay, state.config);
  const storageKey = `${CHILD_VIEWPORT_STATE_PREFIX}${layoutKey}`;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(layoutSnapshot));
  } catch (error) {
    // The child window requires its layout snapshot from same-origin storage.
    return;
  }

  initializeCrossWindow2dReferenceLines(syncChannel, true);
  setCrossWindow2dReferenceLinesEnabled(state.layout.isRefLines);
  setCrossWindowSelectedTool(state.layout.currentTool);
  setCrossWindowActiveViewport(targetIndex, createActiveViewportContext(layout, targetIndex));
  let screenDetailsPromise = Promise.resolve(null);
  if (typeof window.getScreenDetails === 'function') {
    try {
      screenDetailsPromise = window.getScreenDetails().catch(() => null);
    } catch (error) {
      screenDetailsPromise = Promise.resolve(null);
    }
  }

  // Open a same-origin blank popup first so its child marker is set before
  // the application loads, even if deployment redirects drop the query.
  const childWindow = window.open('about:blank', '_blank', popupFeatures);

  if (!childWindow) {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (error) {
      // A stale state entry is harmless and uses a unique layout key.
    }
    return;
  }

  openedChildWindows.push({ window: childWindow, seriesUID: seriesToDisplay.series.SeriesInstanceUID });

  // Keep a reference for positioning while preventing the new window from
  // navigating the viewer tab through window.opener.
  childWindow.name = `${CHILD_VIEWPORT_WINDOW_NAME_PREFIX}${layoutKey}`;
  childWindow.opener = null;
  childWindow.location.replace(getViewportUrl(seriesToDisplay.study.StudyInstanceUID, layoutKey));

  screenDetailsPromise.then((screenDetails) => {
    if (!screenDetails) return;
    const otherScreen = screenDetails.screens.find((screen) => screen !== screenDetails.currentScreen);
    if (!otherScreen || childWindow.closed) return;

    try {
      childWindow.moveTo(Math.round(otherScreen.availLeft), Math.round(otherScreen.availTop));
      childWindow.resizeTo(Math.round(otherScreen.availWidth), Math.round(otherScreen.availHeight));
      childWindow.focus();
    } catch (error) {
      // Browser window placement can be restricted; the opened window remains usable.
    }
  });
};
